import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export const EVENTS_QUERY_KEY = "admin-events";
export const EVENTS_PER_PAGE = 20;

export type EventSortField = "title" | "start_time" | "venue" | "capacity" | "price" | "booked";
export type SortDirection = "asc" | "desc";

export type EventFilterField = "status" | "title" | "venue" | "tag" | "host" | "price" | "capacity" | "booked";
export type EventFilterOperator = "contains" | "equals" | ">=" | "<=" | ">" | "<";

export interface EventFilterTag {
  id: string;
  field: EventFilterField;
  value: string;
  label: string;
  operator?: EventFilterOperator;
}

const FIELD_LABELS: Record<EventFilterField, string> = {
  status: "Status",
  title: "Title",
  venue: "Venue",
  tag: "Tag",
  host: "Host",
  price: "Price",
  capacity: "Capacity",
  booked: "Booked",
};

const EVENT_FILTERS_STORAGE_KEY = "admin-event-filters";
const EVENT_SORT_STORAGE_KEY = "admin-event-sort";

const getInitialEventFilters = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(EVENT_FILTERS_STORAGE_KEY) || null;
  }
  return null;
};

const getInitialEventSort = (): { field: EventSortField; direction: SortDirection } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(EVENT_SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through
      }
    }
  }
  return { field: "start_time", direction: "desc" };
};

// Helper to compare values with operators
const compareWithOperator = (value: number, filterValue: number, operator: EventFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value === filterValue;
    case ">=": return value >= filterValue;
    case "<=": return value <= filterValue;
    case ">": return value > filterValue;
    case "<": return value < filterValue;
    default: return value === filterValue;
  }
};

// Helper to compare strings with operators
const compareStringWithOperator = (value: string, filterValue: string, operator: EventFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value.toLowerCase() === filterValue.toLowerCase();
    case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
    default: return value.toLowerCase().includes(filterValue.toLowerCase());
  }
};

export const useAdminEvents = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  
  const initialSort = getInitialEventSort();
  const [sortField, setSortField] = useState<EventSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort.direction);

  // Filters stored as JSON string in URL with localStorage fallback
  const [filtersParam, setFiltersParamRaw] = useQueryState<string>(
    "eventFilters",
    parseAsString.withOptions({ clearOnDefault: false })
  );

  // Wrapper to persist to localStorage
  const setFiltersParam = useCallback((value: string | null) => {
    if (value) {
      localStorage.setItem(EVENT_FILTERS_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(EVENT_FILTERS_STORAGE_KEY);
    }
    setFiltersParamRaw(value);
  }, [setFiltersParamRaw]);

  // Initialize filters from localStorage on mount (only once)
  useEffect(() => {
    if (filtersParam === null) {
      const stored = getInitialEventFilters();
      if (stored) {
        setFiltersParamRaw(stored);
      }
    }
  }, []); // Only run on mount

  // Parse filters from URL param into FilterTag array
  const filterTags = useMemo<EventFilterTag[]>(() => {
    if (!filtersParam) return [];
    try {
      const parsed = JSON.parse(filtersParam) as { field: EventFilterField; value: string; operator?: EventFilterOperator }[];
      return parsed.map((f, i) => ({
        id: `${f.field}-${i}-${f.value}-${f.operator || "default"}`,
        field: f.field,
        value: f.value,
        label: FIELD_LABELS[f.field],
        operator: f.operator,
      }));
    } catch {
      return [];
    }
  }, [filtersParam]);

  // Derive current status from filters for query key (null means show all)
  const currentStatus = useMemo(() => {
    const statusFilter = filterTags.find(t => t.field === "status");
    return statusFilter?.value || null;
  }, [filterTags]);

  // Add a new filter tag
  const addFilter = useCallback((field: EventFilterField, value: string, operator?: EventFilterOperator) => {
    const current = filterTags.map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    
    // For status field, replace existing status filter instead of adding multiple
    if (field === "status") {
      const updated = current.filter(f => f.field !== "status");
      updated.unshift({ field, value, operator: undefined });
      setFiltersParam(JSON.stringify(updated));
      return;
    }
    
    const exists = current.some(f => f.field === field && f.value.toLowerCase() === value.toLowerCase() && f.operator === operator);
    if (exists) return;
    const updated = [...current, { field, value, operator }];
    setFiltersParam(JSON.stringify(updated));
  }, [filterTags, setFiltersParam]);

  // Remove a filter tag by id
  const removeFilter = useCallback((id: string) => {
    const updated = filterTags
      .filter(t => t.id !== id)
      .map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    setFiltersParam(updated.length > 0 ? JSON.stringify(updated) : null);
  }, [filterTags, setFiltersParam]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFiltersParam(null);
  }, [setFiltersParam]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filtersParam]);

  const handleSort = useCallback((field: EventSortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "asc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(EVENT_SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  // Fetch all events (status filtering is done client-side for simplicity)
  const { data: events = [], isLoading } = useQuery({
    queryKey: [EVENTS_QUERY_KEY, currentStatus],
    queryFn: async () => {
      const queryBuilder = supabase
        .from("events")
        .select(`
          *,
          event_tags(tag)
        `)
        .order("start_time", { ascending: true });

      // Apply status filter at the database level for efficiency (null = show all)
      if (currentStatus === "canceled") {
        queryBuilder.eq("is_canceled", true);
      } else if (currentStatus === "active") {
        queryBuilder.eq("is_archived", false);
        queryBuilder.eq("is_canceled", false);
      } else if (currentStatus === "archived") {
        queryBuilder.eq("is_archived", true);
      }
      // If currentStatus is null, no filter is applied - shows all events

      const { data: eventsData, error: eventsError } = await queryBuilder;
      if (eventsError) throw eventsError;

      // Aggregate summary: one row per event from event_booking_summary view.
      // Aggregating at the DB level avoids the PostgREST 1000-row cap that would
      // silently truncate a raw tickets query on large datasets.
      const eventIds = (eventsData || []).map((e) => e.id);
      const summaryByEvent: Record<
        string,
        { booked: number; present: number; no_show: number; unmarked: number; confirmedTickets: number }
      > = {};
      if (eventIds.length > 0) {
        const { data: summaryRows, error: summaryError } = await supabase
          .from("event_booking_summary")
          .select("event_id, booked, confirmed_tickets, present, no_show, unmarked")
          .in("event_id", eventIds);
        if (summaryError) throw summaryError;
        for (const row of summaryRows || []) {
          summaryByEvent[row.event_id] = {
            booked: row.booked || 0,
            present: row.present || 0,
            no_show: row.no_show || 0,
            unmarked: row.unmarked || 0,
            confirmedTickets: row.confirmed_tickets || 0,
          };
        }
      }

      return eventsData.map((event) => {
        const s = summaryByEvent[event.id];
        return {
          ...event,
          tags: event.event_tags?.map((t: any) => t.tag) || [],
          booked: s?.booked || 0,
          attendance: s
            ? {
                present: s.present,
                no_show: s.no_show,
                unmarked: s.unmarked,
                confirmedTickets: s.confirmedTickets,
              }
            : { present: 0, no_show: 0, unmarked: 0, confirmedTickets: 0 },
        };
      });
    },
  });

  // Client-side filtering and sorting
  const sortedEvents = useMemo(() => {
    if (!events?.length) return [];

    let filtered = [...events];

    // Group filters by field for OR logic within same field
    const filtersByField = filterTags.reduce((acc, tag) => {
      if (!acc[tag.field]) acc[tag.field] = [];
      acc[tag.field].push({ value: tag.value.toLowerCase(), operator: tag.operator });
      return acc;
    }, {} as Record<EventFilterField, { value: string; operator?: EventFilterOperator }[]>);

    // Apply filters
    if (filtersByField.title?.length) {
      filtered = filtered.filter(e => 
        filtersByField.title.some(f => 
          compareStringWithOperator(e.title || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.venue?.length) {
      filtered = filtered.filter(e => 
        filtersByField.venue.some(f => 
          compareStringWithOperator(e.venue || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.tag?.length) {
      filtered = filtered.filter(e => 
        filtersByField.tag.some(f => 
          e.tags?.some((tag: string) => 
            compareStringWithOperator(tag, f.value, f.operator || "contains")
          )
        )
      );
    }

    if (filtersByField.host?.length) {
      filtered = filtered.filter(e => 
        filtersByField.host.some(f => 
          compareStringWithOperator(e.host || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.price?.length) {
      filtered = filtered.filter(e => 
        filtersByField.price.some(f => 
          compareWithOperator(e.price || 0, parseFloat(f.value), f.operator || "equals")
        )
      );
    }

    if (filtersByField.capacity?.length) {
      filtered = filtered.filter(e => 
        filtersByField.capacity.some(f => 
          compareWithOperator(e.capacity || 0, parseInt(f.value), f.operator || "equals")
        )
      );
    }

    if (filtersByField.booked?.length) {
      filtered = filtered.filter(e => 
        filtersByField.booked.some(f => 
          compareWithOperator(e.booked || 0, parseInt(f.value), f.operator || "equals")
        )
      );
    }

    // Sort
    const sorted = filtered.sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      switch (sortField) {
        case "title":
          aVal = a.title?.toLowerCase() || "";
          bVal = b.title?.toLowerCase() || "";
          break;
        case "start_time":
          aVal = a.start_time ? new Date(a.start_time).getTime() : 0;
          bVal = b.start_time ? new Date(b.start_time).getTime() : 0;
          break;
        case "venue":
          aVal = a.venue?.toLowerCase() || "";
          bVal = b.venue?.toLowerCase() || "";
          break;
        case "capacity":
          aVal = a.capacity || 0;
          bVal = b.capacity || 0;
          break;
        case "price":
          aVal = a.price || 0;
          bVal = b.price || 0;
          break;
        case "booked":
          aVal = a.booked || 0;
          bVal = b.booked || 0;
          break;
        default:
          return 0;
      }

      if (aVal === bVal) return 0;
      if (sortDirection === "asc") {
        return aVal < bVal ? -1 : 1;
      }
      return aVal > bVal ? -1 : 1;
    });

    return sorted;
  }, [events, sortField, sortDirection, filterTags]);

  const totalEvents = sortedEvents.length;
  const totalPages = Math.ceil(totalEvents / EVENTS_PER_PAGE);
  const paginatedEvents = sortedEvents.slice(
    (currentPage - 1) * EVENTS_PER_PAGE,
    currentPage * EVENTS_PER_PAGE
  );

  // Actions
  const toggleArchiveEvent = useCallback(async (eventId: string, isArchived: boolean) => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_archived: isArchived })
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Event ${isArchived ? 'archived' : 'unarchived'} successfully`,
      });

      queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY], exact: false });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: `Failed to ${isArchived ? 'archive' : 'unarchive'} event`,
        variant: "destructive",
      });
    }
  }, [queryClient]);

  const toggleCancelEvent = useCallback(async (eventId: string, isCanceled: boolean, reason: string | null) => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_canceled: isCanceled, canceled_reason: isCanceled ? reason : null })
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Event ${isCanceled ? 'canceled' : 'uncanceled'}!`,
      });

      queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY], exact: false });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: `Failed to ${isCanceled ? 'cancel' : 'uncancel'} event`,
        variant: "destructive",
      });
    }
  }, [queryClient]);

  const deleteTag = useCallback(async (eventId: string, tagToDelete: string) => {
    try {
      const { error } = await supabase
        .from('event_tags')
        .delete()
        .eq('event_id', eventId)
        .eq('tag', tagToDelete);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY], exact: false });
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    }
  }, [queryClient]);

  return {
    events: paginatedEvents,
    allEvents: events,
    isLoading,
    isEmpty: !isLoading && events.length === 0,
    // Filters
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    currentStatus,
    // Pagination
    currentPage,
    setCurrentPage,
    totalPages,
    totalEvents,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    // Actions
    toggleArchiveEvent,
    toggleCancelEvent,
    deleteTag,
  };
};
